package top.wcpe.allincore.dsl.compiler.ast

import top.wcpe.allincore.dsl.compiler.source.SourcePosition

/**
 * `theme` 块 AST。
 *
 * tokens 值类型最终仅允许 String / Double / Boolean。整数字面量统一存为 Double，
 * 避免与 dsl-ir themeTokens 的存储约定（M6b Wave 1）出现整数/浮点歧义。
 *
 * 解析期可临时存入 [ThemeValue]（token 引用 / 颜色函数），由 ThemeValueResolverPass 在
 * 编译期解析回字面量后才进入 IR；IR 发射前 tokens 内不应再残留 [ThemeValue]。
 */
data class ThemeAst(
    val tokens: LinkedHashMap<String, Any>,
    val modifierBundles: List<ModifierBundleAst> = emptyList(),
) : AstNode {
    /** 每个显式 token 的定义节点；sidecar 不参与公开构造、copy/componentN 或相等性。 */
    internal var tokenNodes: LinkedHashMap<String, ThemeTokenAst> = linkedMapOf()

    /** theme 块实际范围；sidecar 保持既有两参数主构造 ABI。 */
    internal var sourcePosition: SourcePosition = SourcePosition.UNKNOWN

    override val position: SourcePosition
        get() = sourcePosition

    /** 显式复制解析位置元数据，避免 data class copy 静默丢失 sidecar。 */
    internal fun copySourceMetadataFrom(source: ThemeAst) {
        tokenNodes = LinkedHashMap(source.tokenNodes)
        sourcePosition = source.sourcePosition
    }
}

/** 主题目录展示元数据；独立于视觉 token，不参与主题查找与继承。 */
data class ThemeMetadataAst(
    val displayName: String,
    val group: String,
    val order: Int,
    val previewPrimary: String,
    val previewSurface: String,
    val previewOnSurface: String,
)

/** theme 中单个 `name = value` 定义节点。 */
internal data class ThemeTokenAst(
    val name: String,
    val value: Any,
    val valuePosition: SourcePosition,
    override val position: SourcePosition,
) : AstNode {
    override val range
        get() = position.range.merge(valuePosition.range)
}

/** 颜色函数参数值及其实际源码范围。 */
internal data class ThemeArgument(
    val value: Any,
    override val position: SourcePosition,
) : AstNode

/**
 * theme 字段值的「未解析」中间形态：仅存在于解析期到 ThemeValueResolverPass 解析期之间，
 * 解析后回写为 String/Double/Boolean 字面量，保持 IR themeTokens 的原始值约定。
 */
sealed interface ThemeValue : AstNode {
    /** token 引用别名：`primary = secondary`，编译期解析为目标 token 的字面量。 */
    data class TokenRef(
        val name: String,
        override val position: SourcePosition = SourcePosition.UNKNOWN,
    ) : ThemeValue

    /**
     * 颜色函数调用：如 `lighten(color, 0.1)`，编译期用 ColorMath 求值为 #AARRGGBB。
     * args 元素可为字面量（String/Double）或嵌套的 [ThemeValue]（支持 token 引用 / 嵌套调用）。
     */
    data class ColorCall(
        val name: String,
        val args: List<Any>,
        override val position: SourcePosition = SourcePosition.UNKNOWN,
    ) : ThemeValue
}
